import { AuthStateService } from 'app/core/services/auth-state.service';
import { AppConstants } from 'app/app.constants';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { PostalCodeService } from 'app/core/services/postalCode.service';
import { UserService } from 'app/core/services/user.service';
import { Address, User } from 'app/shared/models/user';
import { ValidateId } from 'app/shared/validators/ValidateId';
import { GenericValidator } from 'app/utils/generic-form-validator';
import { Subject } from 'rxjs';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { AgresSpinnerService } from '../../agres-spinner/agres-spinner.service';
import { ToastrService } from 'ngx-toastr';

type AddressFormGroup = FormGroup<{
  country: FormControl<string>;
  city: FormControl<string>;
  stateOrProvince: FormControl<string>;
  postalCode: FormControl<string>;
}>;

@Component({
  standalone: false,
  selector: 'app-form-atualizacao-cadastro',
  templateUrl: './atualizacao-cadastro.component.html',
  styleUrls: ['./atualizacao-cadastro.component.scss'],
})
export class AtualizacaoCadastroFormComponent implements OnInit {
  @Input() offCanvas: boolean;
  @Input() userId: string;
  @Input() showOffCanvasSubject: Subject<boolean>;
  @Output() modalChange: EventEmitter<boolean>;
  user: User;
  public outsideBrazil: boolean = false;
  public registrarForm: FormGroup<{
    phoneNumber: FormControl<string>;
    company: FormControl<string>;
    addressFormGroup: AddressFormGroup;
  }>;
  private readonly validationMessages: Record<string, Record<string, string>>;
  private readonly authState = inject(AuthStateService);
  private readonly genericValidator: GenericValidator;
  public displayMessage: Record<string, string> = {};
  private errors: string[] = [];
  public disableSubmitButton: boolean;
  public readonly countries: Array<string> = AppConstants.COUNTRY_CODES;
  private phoneUtil: PhoneNumberUtil;
  constructor(
    public fb: FormBuilder,
    public translateService: TranslateService,
    public userService: UserService,
    public postalCodeService: PostalCodeService,
    private readonly spinnerService: AgresSpinnerService,
    private readonly toastr: ToastrService,
  ) {
    this.validationMessages = {
      phoneNumber: {
        required: '',
        maxlength: '',
        minlength: '',
      },
      company: {
        required: '',
        minlength: '',
      },
      country: {
        required: '',
      },
      city: {
        required: '',
        minlength: '',
      },
      stateOrProvince: {
        required: '',
        minlength: '',
      },
      postalCode: {
        required: '',
        minlength: '',
      },
    };

    this.populateValidateObj();

    this.genericValidator = new GenericValidator(this.validationMessages);
    this.phoneUtil = PhoneNumberUtil.getInstance();
  }

  populateValidateObj() {
    const fields = 'global.validate.fields';
    const phoneError = 'global.validate.error-phone-data';
    const userCompanyError = 'global.validate.error-user-data-incorrect-text';
    const document = 'global.validate.error-document-data';
    const identification = 'global.validate.identification-required';
    const keys = [
      fields,
      phoneError,
      userCompanyError,
      identification,
      document,
    ];

    this.translateService.get(keys).subscribe((translations: string[]) => {
      this.validationMessages.phoneNumber.required = translations[phoneError];
      this.validationMessages.company.required = translations[userCompanyError];

      const identificationRequired = translations[identification];
      this.validationMessages.country.required = identificationRequired;
      this.validationMessages.city.required = identificationRequired;
      this.validationMessages.stateOrProvince.required = identificationRequired;
      this.validationMessages.postalCode.required = identificationRequired;
      const documentData = translations[document];
    });
  }

  ngOnInit() {
    this.userService.getUserByEmail(this.userId).subscribe(
      (user: User) => {
        this.user = user;
        this.registrarForm.valueChanges.subscribe(() => {
          this.displayMessage = this.genericValidator.processMessages(
            this.registrarForm,
          );
        });

        const addressFormGroup = this.registrarForm.get('addressFormGroup');

        addressFormGroup.get('country').valueChanges.subscribe((country) => {
          this.outsideBrazil = country !== 'BRA';
        });
        this.setDefaultCountry();
      },
      (e) => {
        this.translateService.get('profile.failed').subscribe((res: string) => {
          this.toastr.warning(res, '', {
            positionClass: 'toast-top-full-width',
            disableTimeOut: false,
          });
        });
      },
    );

    this.disableSubmitButton = false;
  }

  formatPhoneNumber(event: InputEvent): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value.replace(/\D/g, '');

    try {
      const number = this.phoneUtil.parse(value, 'BRA');
      const formatted = this.phoneUtil.format(
        number,
        PhoneNumberFormat.NATIONAL,
      );
      this.registrarForm
        .get('phoneNumber')
        ?.setValue(formatted, { emitEvent: false });
    } catch (e) {
      this.registrarForm
        .get('phoneNumber')
        ?.setValue(value, { emitEvent: false });
    }
  }

  buildRegistrationForm(): {
    phoneNumber: FormControl<string>;
    company: FormControl<string>;
    addressFormGroup: FormGroup<{
      country: FormControl<string>;
      city: FormControl<string>;
      stateOrProvince: FormControl<string>;
      postalCode: FormControl<string>;
    }>;
  } {
    let city: string,
      stateOrProvince: string,
      country: string,
      postalCode: string;

    if (this.user.address) {
      postalCode = this.user.address.postalCode ?? '';
      city = this.user.address.city ?? '';
      stateOrProvince = this.user.address.stateOrProvince ?? '';
      country = this.user.address.country ?? '';
    }

    return {
      company: new FormControl(this.user['company'], [Validators.required]),
      addressFormGroup: this.fb.group({
        country: [country, [Validators.required]],
        city: [city, [Validators.required]],
        stateOrProvince: [stateOrProvince, [Validators.required]],
        postalCode: [
          postalCode,
          [Validators.required, Validators.minLength(2)],
        ],
      }),
      phoneNumber: new FormControl(this.user.phoneNumber ?? '', [
        Validators.required,
      ]),
    };
  }

  isCelular(): boolean {
    return this.registrarForm.controls.phoneNumber.value.length === 11;
  }

  removePhoneNumberMask(phoneNumber: string) {
    let telefoneSemMask = phoneNumber.replace('(', '');
    telefoneSemMask = telefoneSemMask.replace(')', '');
    telefoneSemMask = telefoneSemMask.replace('-', '');
    telefoneSemMask = telefoneSemMask.replace(' ', '');

    return telefoneSemMask;
  }
  popularTelefone(telefoneForm: string): string {
    telefoneForm = telefoneForm.replace(/\D/g, '');

    if (telefoneForm.length === 10) {
      return `(${telefoneForm.slice(0, 2)}) ${telefoneForm.slice(2, 6)}-${telefoneForm.slice(6, 10)}`;
    } else if (telefoneForm.length === 11) {
      return `(${telefoneForm.slice(0, 2)}) ${telefoneForm.slice(2, 7)}-${telefoneForm.slice(7, 11)}`;
    } else {
      return telefoneForm;
    }
  }

  checkCep(postalCode: Event) {
    const postalCodeValue = (postalCode.target as HTMLInputElement).value;
    if (postalCodeValue) {
      this.postalCodeService.searchCEP(postalCodeValue).subscribe(
        (dados) => {
          this.addPostalCodeDataIntoForm(
            dados,
            this.registrarForm.controls.addressFormGroup,
          );
        },
        (error) => {
          if (error) {
            this.translateService
              .get('global.validate.error-cep')
              .subscribe((res: string) => {
                this.toastr.error(res, '', {
                  positionClass: 'toast-top-full-width',
                  disableTimeOut: false,
                });
              });
          }
        },
      );
    }
  }

  addPostalCodeDataIntoForm(
    postalCodeData: Address | null,
    form: AddressFormGroup,
  ) {
    if (postalCodeData instanceof Object) {
      if ('erro' in postalCodeData) {
        this.translateService
          .get('global.validate.error-cep')
          .subscribe((res: string) => {
            this.toastr.error(res, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            });
          });
      } else {
        if (postalCodeData.city) {
          form.get('city').disable();
        }

        if (postalCodeData.uf) {
          form.get('stateOrProvince').disable();
        }
        form.patchValue({
          city: postalCodeData.city,
          stateOrProvince: postalCodeData.uf,
        });
      }
    }
  }

  buildUserObject(): User {
    const addressFormGroup = this.registrarForm.controls.addressFormGroup;
    const user = {
      id: this.user.id,
      name: this.user.name,
      role: this.user.role,
      company: this.registrarForm.controls.company.value,
      address: {
        postalCode: addressFormGroup.get('postalCode').value,
        country: addressFormGroup.get('country').value,
        city: addressFormGroup.get('city').value,
        stateOrProvince: addressFormGroup.get('stateOrProvince').value,
      },
      phoneNumber: this.removePhoneNumberMask(
        this.registrarForm.controls.phoneNumber.value,
      ),
    };
    return user;
  }

  updateUser() {
    this.displayMessage = this.genericValidator.processMessages(
      this.registrarForm,
    );

    this.translateService
      .get('profile.updating-your-data')
      .subscribe((res: string) => {
        this.spinnerService.toShow(res);
      });

    if (this.registrarForm.valid && this.registrarForm.dirty) {
      const user = this.buildUserObject();

      this.disableSubmitButton = true;
      this.userService.patchUser(user).subscribe(
        () => {
          this.onSaveComplete();
        },
        (error) => {
          this.onError(error);
        },
      );
    }
  }

  onSaveComplete() {
    this.errors = [];
    this.disableSubmitButton = true;
    this.authState.clearSession();

    this.spinnerService.toHide();
    window.document.location.reload();
  }

  onError(fail: Record<string, string>) {
    this.disableSubmitButton = false;
    this.errors = [fail.error];

    this.translateService
      .get('error.500.error-occurred-try-again')
      .subscribe((res: string) => {
        this.toastr.error(this.errors[0], res, {
          positionClass: 'toast-top-full-width',
          disableTimeOut: false,
        });
      });
  }

  public hideOffCanvas(): void {
    this.showOffCanvasSubject.next(false);
    window.localStorage.setItem(
      AppConstants.KEYS_LOCAL_STORAGE.HIDE_MODAL,
      'true',
    );
  }
  setDefaultCountry() {
    const language = localStorage.getItem(
      AppConstants.KEYS_LOCAL_STORAGE.ISO_LANG,
    );
    if (language === AppConstants.LANGUAGES.PT_BR) {
      const countryControl =
        this.registrarForm.controls.addressFormGroup.controls.country;
      if (countryControl && !countryControl.value) {
        countryControl.setValue('BRA');
      }
    }
  }
}
